/**
 * Shared URL `messageId` for Order 31E Message Detail Drawer.
 * Preserves other report filter params; open pushes history for Back-to-close.
 */
import { useCallback, useEffect, useEffectEvent, useState, startTransition } from "react";
import { useSearchParams } from "react-router-dom";
import type { ReportMessageDetail } from "@multi-chat/shared";
import { getReportMessageDetail } from "../../api/reports-api.js";

const MESSAGE_ID_RE = /^\d+$/;

export function parseReportMessageIdParam(raw: string | null): string | undefined {
  if (raw == null) return undefined;
  const trimmed = raw.trim();
  if (!MESSAGE_ID_RE.test(trimmed)) return undefined;
  return trimmed;
}

export function useReportMessageDetail(input: {
  organizationId: number;
  canAccess: boolean;
}): {
  messageId: string | undefined;
  detail: ReportMessageDetail | null;
  loading: boolean;
  error: unknown;
  openMessage: (messageId: string) => void;
  closeMessage: () => void;
  refresh: () => void;
} {
  const [searchParams, setSearchParams] = useSearchParams();
  const messageId = parseReportMessageIdParam(searchParams.get("messageId"));
  const [detail, setDetail] = useState<ReportMessageDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  const openMessage = useCallback(
    (nextId: string) => {
      if (!MESSAGE_ID_RE.test(nextId)) return;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const current = next.get("messageId");
          if (current === nextId) return prev;
          next.set("messageId", nextId);
          return next;
        },
        { replace: Boolean(messageId) },
      );
    },
    [messageId, setSearchParams],
  );

  const closeMessage = useCallback(() => {
    setSearchParams(
      (prev) => {
        if (!prev.has("messageId")) return prev;
        const next = new URLSearchParams(prev);
        next.delete("messageId");
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const load = useEffectEvent(async (signal: AbortSignal, id: string) => {
    if (!input.canAccess) return;
    setLoading(true);
    setError(null);
    try {
      const next = await getReportMessageDetail(input.organizationId, id, signal);
      if (signal.aborted) return;
      startTransition(() => {
        setDetail(next);
      });
    } catch (err) {
      if (signal.aborted) return;
      setDetail(null);
      setError(err);
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  });

  useEffect(() => {
    if (!input.canAccess || messageId == null) {
      setDetail(null);
      setError(null);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    void load(controller.signal, messageId);
    return () => controller.abort();
  }, [input.canAccess, input.organizationId, messageId, refreshToken]);

  return {
    messageId,
    detail,
    loading,
    error,
    openMessage,
    closeMessage,
    refresh: () => setRefreshToken((n) => n + 1),
  };
}
